import { cn } from '@/lib/utils'
import {
    IconButton,
    InputAdornment,
    InputLabelProps,
    TextField,
    TextFieldProps,
} from '@mui/material'
import 'cleave.js/dist/addons/cleave-phone.br'
import { CleaveOptions } from 'cleave.js/options'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { useThemeStore } from '../ToggleTheme'
import { Tooltip } from '../ui/tooltip'
import { CleaveTextField } from './CleaveTextField'

type FormsProps = Omit<UseControllerProps, 'control'> &
    Omit<TextFieldProps, 'name' | 'value' | 'ref' | 'hidden'> & {
        hidden?: boolean
        hiddenClock?: boolean
        hiddenPassword?: boolean
        name: string
        currency?: 'BRL' | 'USD' | 'EUR' | 'JPY' | 'GBP' | 'CAD' | 'AUD'
        uppercase?: boolean
        unHelperText?: boolean
        options?: CleaveOptions
        /** Se type number, define o número mínimo, ainda permite valor em branco, caso contrário limita o length do valor */
        min?: number
        /** Se type number, define o número máximo, caso contrário limita o length do valor */
        max?: number
    }

export const FormField: React.FC<FormsProps> = ({
    name,
    onChange,
    rules,
    type = 'text',
    hidden,
    hiddenClock,
    hiddenPassword,
    options,
    variant = 'filled',
    defaultValue,
    InputLabelProps: inputLabelProps,
    InputProps,
    size = 'medium',
    currency,
    uppercase,
    unHelperText,
    disabled,
    shouldUnregister,
    required,
    min,
    max,
    className,
    ...rest
}) => {
    const { hash } = useLocation()
    const { control } = useFormContext()
    const theme = useThemeStore(state => state.theme)

    const clock = hiddenClock || false
    const pass = hiddenPassword || false

    const [passw, setPass] = useState<boolean>(pass)

    const handlePassword = () => {
        setPass(prev => !prev)
    }

    const style = useMemo(() => {
        if (hidden) {
            return {
                display: 'none',
            }
        }

        const inner = {
            '[type="time"]::-webkit-calendar-picker-indicator': {
                display: 'none',
            },
        }

        if (clock) {
            return {
                ...inner,
                ...rest.sx,
            }
        }

        return {
            colorScheme: theme,
            ...rest.sx,
        }
    }, [clock, hidden, rest.sx])

    const hasDisabled = disabled || (control as any)._options?.disabled

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            onChange({
                ...event,
                target: {
                    ...event.target,
                    value: event.target.value,
                },
            })
        }
    }

    const handleInputLabelProps = (value: string | undefined, type: string) => {
        const inputLabelPropsValue: InputLabelProps = {}
        if (inputLabelProps) {
            if (value && value.length > 0) {
                inputLabelProps.shrink = true
                inputLabelPropsValue.shrink = true

                return inputLabelProps
            }

            if (type === 'time' || type === 'date' || type === 'month') {
                inputLabelProps.shrink = true
                inputLabelPropsValue.shrink = true

                return inputLabelProps
            }

            return inputLabelProps
        }

        if (value && value.length > 0) {
            inputLabelPropsValue.shrink = true
        }

        if (type === 'time' || type === 'date' || type === 'month') {
            inputLabelPropsValue.shrink = true
        }

        return inputLabelPropsValue
    }

    const getCurrency = (value: string | number) => {
        if (!value) {
            return ''
        }

        if (typeof value === 'string') {
            value = Number(value.replace(/\D/g, ''))
        }

        return (+value / 100).toLocaleString(currency, {
            style: 'currency',
            currency,
        })
    }

    const PasswordEndAdornment = () => (
        <InputAdornment position="end">
            <IconButton
                aria-label="toggle password visibility"
                onClick={handlePassword}
            >
                {passw ? <EyeOffIcon /> : <EyeIcon />}
            </IconButton>
        </InputAdornment>
    )

    const isValidUrl = (value: string) => {
        if (!disabled || !value) return false
        try {
            new URL(value)
            return true
        } catch (_) {
            return false
        }
    }

    return (
        <Controller
            name={name}
            rules={rules}
            control={control}
            defaultValue={defaultValue || (required ? '' : undefined)}
            shouldUnregister={shouldUnregister}
            render={({
                field: { onChange, value, ...field },
                fieldState: { error },
            }) => (
                <Tooltip
                    title={(() => {
                        if (unHelperText && error?.message) return error.message
                        return ''
                    })()}
                >
                    <TextField
                        {...field}
                        {...rest}
                        className={cn({ 'opacity-75': hasDisabled }, className)}
                        required={required}
                        error={!hidden && !!error}
                        size={size}
                        sx={style}
                        SelectProps={{
                            ...rest.SelectProps,
                            MenuProps: {
                                ...rest.SelectProps?.MenuProps,
                                PaperProps: {
                                    sx: {
                                        maxHeight: '25rem',
                                        ...rest.SelectProps?.MenuProps?.sx,
                                    },
                                    ...rest.SelectProps?.MenuProps?.PaperProps,
                                },
                            },
                        }}
                        type={passw ? 'password' : type}
                        value={(() => {
                            if (isValidUrl(value)) return ''
                            if (currency)
                                return getCurrency(value || defaultValue || '')
                            if (value === null) return ''
                            return value || defaultValue || ''
                        })()}
                        variant={variant}
                        fullWidth
                        disabled={hasDisabled}
                        inputProps={{
                            ...(rest.inputProps ? rest.inputProps : {}),
                            'data-testid': name,
                            ...(options ? { options } : {}),
                        }}
                        InputProps={{
                            ...InputProps,
                            endAdornment: pass ? (
                                <PasswordEndAdornment />
                            ) : (
                                InputProps?.endAdornment
                            ),
                            ...(options
                                ? {
                                      inputComponent: CleaveTextField as any,
                                  }
                                : {}),
                            startAdornment: isValidUrl(value) ? (
                                <InputAdornment position="start">
                                    {InputProps?.startAdornment}
                                    <a
                                        href={value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ textDecoration: 'underline' }}
                                    >
                                        {value}
                                    </a>
                                </InputAdornment>
                            ) : (
                                InputProps?.startAdornment
                            ),
                        }}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                            if (uppercase) {
                                event.target.value =
                                    event.target.value.toUpperCase()
                            }

                            if (event.target.value && min) {
                                if (type === 'number') {
                                    if (Number(event.target.value) < min) {
                                        return
                                    }
                                } else if (event.target.value.length < min) {
                                    return
                                }
                            }

                            if (event.target.value && max) {
                                if (type === 'number') {
                                    if (Number(event.target.value) > max) {
                                        return
                                    }
                                } else if (event.target.value.length > max) {
                                    return
                                }
                            }

                            onChange(event)
                            handleChange(event)
                        }}
                        helperText={(() => {
                            if (error && !unHelperText) return error.message

                            if (rest?.helperText) return rest.helperText

                            if (unHelperText) return null

                            return ' '
                        })()}
                        InputLabelProps={handleInputLabelProps(value, type)}
                        autoFocus={hash === `#${name}`}
                    />
                </Tooltip>
            )}
        />
    )
}
